/*
 * useMapRecenter
 *
 * Single owner of map-recenter decisions: every recenter goes through one of
 * the intents returned here; RecenterAutomaticallyMap applies the resulting
 * centerLatLonMap to the Leaflet map.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  buildVesselsGeojson,
  vesselRecenterPoint,
} from "@/utils/vesselHelper";
import { nowMs } from "@/utils/appNow";
import { loadStartView, saveStartView } from "@/features/map/startView";

dayjs.extend(utc);

const isFiniteNum = (n) => Number.isFinite(Number(n));

const SINGLE_VESSEL_ZOOM = 5;
// Below any minZoom: Leaflet clamps it, so fleets always start fully zoomed out
const FLEET_ZOOM = 0;
// Smaller shifts of the fleet center since the last login keep the remembered start view (no jump)
const START_VIEW_TOLERANCE_DEG = 10;

const wrapLon = (lon) => ((((lon + 180) % 360) + 360) % 360) - 180;

// Center of the shortest longitude arc covering all positions: the far side of the largest gap, so the antimeridian is no edge
export function fleetCenter(lonlats) {
  const valid = (lonlats ?? []).filter(
    (p) => Array.isArray(p) && isFiniteNum(p[0]) && isFiniteNum(p[1]),
  );
  if (valid.length === 0) return null;
  const lons = valid.map((p) => wrapLon(Number(p[0]))).sort((a, b) => a - b);
  const lats = valid.map((p) => Number(p[1]));
  let gapEnd = 0;
  let maxGap = lons[0] + 360 - lons[lons.length - 1];
  for (let i = 1; i < lons.length; i++) {
    if (lons[i] - lons[i - 1] > maxGap) {
      maxGap = lons[i] - lons[i - 1];
      gapEnd = i;
    }
  }
  const arc = 360 - maxGap;
  return [
    (Math.min(...lats) + Math.max(...lats)) / 2,
    wrapLon(lons[gapEnd] + arc / 2),
  ];
}

const isNear = (a, b) =>
  Math.abs(a[0] - b[0]) <= START_VIEW_TOLERANCE_DEG &&
  Math.abs(wrapLon(a[1] - b[1])) <= START_VIEW_TOLERANCE_DEG;

export default function useMapRecenter({ lastPositionByVesselId, user } = {}) {
  const [startView] = useState(() => loadStartView(user));
  const [centerLatLonMap, setCenterLatLonMap] = useState(startView?.center ?? [30, -45]);
  const [centerZoom, setCenterZoom] = useState(startView?.zoom ?? null);

  // zoom: optional target zoom; null keeps the current one
  const recenterToPoint = useCallback((latlng, zoom = null) => {
    if (!Array.isArray(latlng) || latlng.length !== 2) return;
    const [lat, lng] = latlng;
    if (!isFiniteNum(lat) || !isFiniteNum(lng)) return;
    setCenterLatLonMap([Number(lat), Number(lng)]);
    setCenterZoom(zoom == null ? null : Number(zoom));
  }, []);

  const lastPositionRef = useRef(lastPositionByVesselId);
  lastPositionRef.current = lastPositionByVesselId;
  const recenterToVesselLastPosition = useCallback(
    (vesselId) => {
      const lonlat = lastPositionRef.current?.[Number(vesselId)];
      if (!Array.isArray(lonlat) || lonlat.length !== 2) return;
      recenterToPoint([lonlat[1], lonlat[0]]);
    },
    [recenterToPoint],
  );

  // Once per login: single-vessel accounts start close on their vessel, fleets centered on the whole fleet.
  const autoCenteredRef = useRef(false);
  useEffect(() => {
    const positions = Object.values(lastPositionByVesselId ?? {});
    if (positions.length === 0) {
      autoCenteredRef.current = false;
      return;
    }
    if (autoCenteredRef.current) return;
    autoCenteredRef.current = true;
    const center = fleetCenter(positions);
    if (!center) return;
    const zoom = positions.length === 1 ? SINGLE_VESSEL_ZOOM : FLEET_ZOOM;
    saveStartView(user, { center, zoom });
    if (startView && startView.zoom === zoom && isNear(startView.center, center)) return;
    recenterToPoint(center, zoom);
  }, [lastPositionByVesselId, recenterToPoint, startView, user]);

  const [routeRecenterRequest, setRouteRecenterRequest] = useState({
    nonce: 0,
    routeId: null,
  });
  const recenterToRoute = useCallback(
    (routeId = null) =>
      setRouteRecenterRequest((prev) => ({ nonce: prev.nonce + 1, routeId })),
    [],
  );

  return {
    centerLatLonMap,
    centerZoom,
    recenterToPoint,
    recenterToVesselLastPosition,
    recenterToRoute,
    routeRecenterRequest,
  };
}

// Resolves recenterToRoute requests to the route's interpolated position at
// the slider time. Separate hook: its inputs exist later in MainComponent.
export function useRouteRecenterResolver({
  routeRecenterRequest,
  selectedPlanRoutes,
  draftRoutes,
  sliderPosition,
  recenterToPoint,
}) {
  const lastNonceRef = useRef(routeRecenterRequest?.nonce ?? 0);
  useEffect(() => {
    const { nonce, routeId } = routeRecenterRequest ?? {};
    if (nonce == null || nonce === lastNonceRef.current) return;

    // Request stays unconsumed until the clicked route's geometry is loaded.
    const idEq = (a, b) => String(a) === String(b);
    const route =
      (selectedPlanRoutes ?? []).find((r) =>
        idEq(r?.points?.routeId, routeId),
      ) ??
      (draftRoutes ?? []).find((r) => idEq(r?.points?.routeId, routeId)) ??
      null;
    if (!route) return;

    // Same interpolation as the icon — the nearest waypoint can sit far off.
    const sliderMs = dayjs.utc(sliderPosition?.label).valueOf();
    let coords = null;
    if (Number.isFinite(sliderMs)) {
      const [vessel] = buildVesselsGeojson({ routes: [route], sliderPosition });
      coords =
        vessel?.points?.geojson?.features?.[0]?.geometry?.coordinates ?? null;
    }
    if (!coords) {
      coords = vesselRecenterPoint(
        route,
        Number.isFinite(sliderMs) ? sliderMs : nowMs(),
      );
    }
    if (
      Array.isArray(coords) &&
      isFiniteNum(coords[0]) &&
      isFiniteNum(coords[1])
    ) {
      lastNonceRef.current = nonce;
      recenterToPoint([Number(coords[1]), Number(coords[0])]);
    }
  }, [
    routeRecenterRequest,
    selectedPlanRoutes,
    draftRoutes,
    sliderPosition,
    recenterToPoint,
  ]);
}
